import { Controller } from "@hotwired/stimulus"
import { usesSidebarLayout } from "../utils/layout_media"

// Detail modal for contacts/calendar rows. Attach to a container; any child
// with data-detail-template-id opens a panel filled with that template's
// content, and any child with data-detail-url lazy-fetches the panel HTML on
// first open (cached per URL afterwards) — so lists don't have to inline one
// rendered panel per row. On desktop (when sidebar value is true) contact
// rows open in a right sidebar; otherwise reuses the email-modal overlay
// chrome.
export default class extends Controller {
    static values = { sidebar: { type: Boolean, default: false } }

    connect() {
        this._fetched = new Map()
        this._onClick = (e) => {
            // Modified clicks belong to the browser: several triggers are real
            // links (tree cards carry an href to the profile), so cmd/ctrl or
            // shift click should still open it in a tab or window.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
            let trigger = e.target.closest("[data-detail-template-id], [data-detail-url]")
            if (!trigger || !this.element.contains(trigger)) return
            e.preventDefault()
            e.stopPropagation()
            this._open(trigger)
        }
        this._onResize = () => {
            if (this._sidebarEl && !this._useSidebar()) this._closeSidebar()
        }
        // Saving the contact edit form replaces the whole detail card with a
        // turbo_stream. The kebab is hoisted OUT of that card into the actions
        // bar, so the replacement arrives carrying a second one — re-run the
        // hoist afterwards to drop the stale copy. _mountActionsMenu is a
        // no-op when the panel holds no kebab, so unrelated streams are safe.
        this._onStreamRender = (event) => {
            let render = event.detail.render
            event.detail.render = async (stream) => {
                await render(stream)
                if (this._sidebarEl) this._mountActionsMenu(this._sidebarEl.querySelector(".detail-sidebar-content"))
            }
        }
        // One right sidebar at a time (msg-detail's "jane:open-conversation").
        this._onCloseOthers = (event) => {
            if (event.detail?.except !== this && this._sidebarEl) this._closeSidebar()
        }
        this.element.addEventListener("click", this._onClick)
        window.addEventListener("resize", this._onResize)
        document.addEventListener("turbo:before-stream-render", this._onStreamRender)
        document.addEventListener("jane:detail-sidebars-close", this._onCloseOthers)
    }

    // Open the panel for a trigger whose click never reaches this element: the
    // task outline's chips have to stop their click (anywhere else in the title
    // wrap starts an edit), so the outline hands the chip over as an event
    // instead. Same _open as a row click, so a second click on the same chip
    // closes it, exactly as on /network.
    openFromEvent(event) {
        let trigger = event?.detail?.trigger
        if (!trigger?.dataset?.detailUrl) return
        this._open(trigger)
    }

    // Dismiss from inside the panel (e.g. "Unlink from task" on a company card)
    // without reaching into this controller's scope.
    close() {
        this._closeSidebar()
        this._closeModal()
    }

    // Re-read the OPEN panel because the thing it describes just changed
    // elsewhere on the page.
    //
    // The family tree edits a person in its own modal and the panel beside it
    // displays the result, so that is now the normal loop - and the panel used
    // to sit there showing the old dates until the page was reloaded. A member
    // reported it as "na refresh zichtbaar": she could not tell whether her
    // edit had saved, which is the worst thing this app can do to somebody
    // transcribing a family document.
    //
    // Also drops the cached copy for that url, or reopening the panel would
    // serve the stale one straight from _fetched.
    refreshDetail(event) {
        let url = event?.detail?.url
        if (url) this._fetched.delete(url)
        let trigger = this._activeTrigger
        if (!trigger || !trigger.dataset.detailUrl) return
        if (url && trigger.dataset.detailUrl !== url) return

        this._fetched.delete(trigger.dataset.detailUrl)
        this._fetchDetail(trigger)
    }

    disconnect() {
        this.element.removeEventListener("click", this._onClick)
        window.removeEventListener("resize", this._onResize)
        document.removeEventListener("turbo:before-stream-render", this._onStreamRender)
        document.removeEventListener("jane:detail-sidebars-close", this._onCloseOthers)
        this._closeModal()
        if (this._sidebarEl) this._closeSidebar()
    }

    _useSidebar() {
        return this.sidebarValue && usesSidebarLayout()
    }

    _open(trigger) {
        if (this._useSidebar() && this._sidebarEl && this._activeTrigger === trigger) {
            this._closeSidebar()
            return
        }

        let content = this._localContent(trigger)
        if (content) {
            this._show(content, trigger)
        } else if (trigger.dataset.detailUrl) {
            this._show(this._loadingContent(), trigger)
            this._fetchDetail(trigger)
        }
    }

    // Content available without a network round-trip: a previously fetched
    // panel for this URL, or an inline server-rendered template.
    _localContent(trigger) {
        let url = trigger.dataset.detailUrl
        if (url && this._fetched.has(url)) return this._fetched.get(url).cloneNode(true)

        let templateId = trigger.dataset.detailTemplateId
        if (!templateId) return null
        let template = document.getElementById(templateId)
        return template?.content ? template.content.cloneNode(true) : null
    }

    _fetchDetail(trigger) {
        let url = trigger.dataset.detailUrl
        let token = (this._loadToken = (this._loadToken || 0) + 1)
        fetch(url, { headers: { "Accept": "text/html" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((html) => {
                let template = document.createElement("template")
                template.innerHTML = html
                this._fetched.set(url, template.content)
                if (token !== this._loadToken) return
                this._replaceContent(template.content.cloneNode(true))
            })
            .catch(() => {
                if (token !== this._loadToken) return
                this._replaceContent(this._errorContent())
            })
    }

    _loadingContent() {
        let node = document.createElement("div")
        node.className = "msg-detail-loading"
        node.innerHTML = `
            <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
            <span>Loading…</span>`
        return node
    }

    _errorContent() {
        let node = document.createElement("div")
        node.className = "msg-detail-loading msg-detail-loading--error"
        node.innerHTML = "<span>Could not load these details - please try again.</span>"
        return node
    }

    // Swap the open panel's content in place (used when a lazy fetch lands).
    _replaceContent(node) {
        let holder = this._sidebarEl?.querySelector(".detail-sidebar-content") ||
            this._overlay?.querySelector(".email-modal-content")
        if (holder) holder.replaceChildren(node)
        this._mountActionsMenu(holder)
    }

    _show(content, trigger) {
        if (this._useSidebar()) {
            this._openSidebar(content, trigger)
        } else {
            this._openModal(content)
        }
    }

    _openSidebar(content, trigger) {
        this._closeModal()
        this._hideJaneSidebar()

        let phone = document.querySelector(".assistant-phone")
        if (!phone) {
            this._openModal(content)
            return
        }

        if (!this._sidebarEl) {
            let sidebar = document.createElement("aside")
            sidebar.className = "detail-sidebar"
            sidebar.setAttribute("role", "complementary")
            sidebar.setAttribute("aria-label", "Contact details")
            sidebar.innerHTML = `
                <div class="detail-sidebar-actions">
                    <button type="button" class="detail-sidebar-close assistant-header-btn assistant-filter-toggle assistant-avatar" aria-label="Close">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>
                <div class="detail-sidebar-content"></div>`
            sidebar.querySelector(".detail-sidebar-close").addEventListener("click", () => this._closeSidebar())
            phone.appendChild(sidebar)
            phone.classList.add("is-detail-open")
            this._sidebarEl = sidebar
            this._bindKeydown()
        }

        this._sidebarEl.querySelector(".detail-sidebar-content").replaceChildren(content)
        this._mountActionsMenu(this._sidebarEl.querySelector(".detail-sidebar-content"))

        if (this._activeTrigger) this._activeTrigger.classList.remove("is-selected")
        this._activeTrigger = trigger
        if (trigger) trigger.classList.add("is-selected")
    }

    _openModal(content) {
        this._closeSidebar()
        this._closeModal()

        let overlay = document.createElement("div")
        overlay.className = "email-modal-overlay"
        overlay.innerHTML = `
            <div class="email-modal" role="dialog" aria-modal="true">
                <button type="button" class="email-modal-close" aria-label="Close">
                    <i class="bi bi-x-lg"></i>
                </button>
                <div class="email-modal-content"></div>`
        overlay.querySelector(".email-modal-content").appendChild(content)
        // A panel's own actions belong beside the close button here too. The
        // sidebar has always hoisted them; the modal left them wherever the
        // partial put them, so the same panel had a tidy header at one width
        // and a stack of buttons at the foot of the content at another.
        this._hoistMenu(content, overlay.querySelector(".email-modal"), overlay.querySelector(".email-modal-close"))

        overlay.addEventListener("click", (e) => {
            if (e.target === overlay || e.target.closest(".email-modal-close")) this._closeModal()
        })
        this._bindKeydown()

        document.body.appendChild(overlay)
        document.body.classList.add("email-modal-open")
        this._overlay = overlay
    }

    _closeSidebar() {
        if (this._activeTrigger) {
            this._activeTrigger.classList.remove("is-selected")
            this._activeTrigger = null
        }
        if (this._sidebarEl) {
            this._unmountActionsMenu()
            this._sidebarEl.remove()
            this._sidebarEl = null
        }
        document.querySelector(".assistant-phone")?.classList.remove("is-detail-open")
        this._unbindKeydown()
        this._restoreJaneSidebar()
    }

    _mountActionsMenu(content) {
        if (!content?.querySelector(".contact-detail-menu") || !this._sidebarEl) return
        let actions = this._sidebarEl.querySelector(".detail-sidebar-actions")
        let closeBtn = actions?.querySelector(".detail-sidebar-close")
        if (!actions || !closeBtn) return
        this._unmountActionsMenu()
        // The view-mode button (sidebar_views_controller) belongs AFTER the
        // card's own actions. It is inserted by a MutationObserver, so it can
        // land while the card is still loading - before this hoist - and the
        // actions then went in front of close but behind it: one order when
        // the fetch was fast, another when it was slow.
        let before = actions.querySelector(":scope > .sidebar-view-menu") || closeBtn
        this._hoistMenu(content, actions, before)
    }

    // Move a panel's own kebab up next to the close button, keeping the node
    // itself - it carries its own Stimulus controllers, which is what lets it
    // keep working somewhere else in the document.
    _hoistMenu(content, container, before) {
        let menu = content?.querySelector?.(".contact-detail-menu")
        if (!menu || !container || !before) return

        container.insertBefore(menu, before)
    }

    _unmountActionsMenu() {
        this._sidebarEl?.querySelectorAll(".detail-sidebar-actions .contact-detail-menu")
            .forEach((el) => el.remove())
    }

    _hideJaneSidebar() {
        let jane = document.querySelector(".detail-sidebar--jane")
        if (!jane) return
        let ctrl = this.application.getControllerForElementAndIdentifier(jane, "jane-sidebar")
        if (ctrl) ctrl.hideForDetail()
        else jane.remove()
    }

    _restoreJaneSidebar() {
        document.dispatchEvent(new CustomEvent("jane-sidebar:restore"))
    }

    _closeModal() {
        if (this._overlay) {
            this._overlay.remove()
            this._overlay = null
        }
        document.body.classList.remove("email-modal-open")
        if (!this._sidebarEl) this._unbindKeydown()
    }

    _bindKeydown() {
        if (this._onKeydown) return
        this._onKeydown = (e) => {
            if (e.key === "Escape") {
                this._closeSidebar()
                this._closeModal()
            }
        }
        document.addEventListener("keydown", this._onKeydown)
    }

    _unbindKeydown() {
        if (!this._onKeydown) return
        document.removeEventListener("keydown", this._onKeydown)
        this._onKeydown = null
    }
}
